import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React from 'react';
import type { BgColors, Colors } from '../../constants';
import { TextBadgeCompact, TextBoxEditorTitle } from '../Typography';

export type BadgeVariant =
  | 'primary'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'red'
  | 'dark'
  | 'grey'
  | 'disabled';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: BadgeVariant;
  label: string;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
  round?: boolean;
  size?: 'sm' | 'md';
}

const badgeStyles: Record<
  BadgeVariant,
  { textColor: Colors; backgroundColor: BgColors }
> = {
  primary: {
    textColor: 'text-ds-primary-300',
    backgroundColor: 'bg-ds-natural-100',
  },
  blue: {
    textColor: 'text-ds-primary-500',
    backgroundColor: 'bg-ds-primary-100',
  },
  green: {
    textColor: 'text-ds-accent2-500',
    backgroundColor: 'bg-ds-accent2-100',
  },
  yellow: {
    textColor: 'text-ds-secondary-500',
    backgroundColor: 'bg-ds-secondary-100',
  },
  red: {
    textColor: 'text-ds-danger-500',
    backgroundColor: 'bg-ds-danger-100',
  },
  dark: {
    textColor: 'text-ds-accent3-200',
    backgroundColor: 'bg-ds-natural-600',
  },
  grey: {
    textColor: 'text-ds-natural-600',
    backgroundColor: 'bg-ds-natural-150',
  },
  disabled: {
    textColor: 'text-ds-natural-200',
    backgroundColor: 'bg-ds-natural-200-a',
  },
};

const sizeStyles: Record<'sm' | 'md', string> = {
  sm: 'py-px px-1',
  md: 'px-1.5',
};

export const Badge = ({
  variant = 'primary',
  label,
  leftIcon,
  rightIcon,
  round,
  className,
  size = 'md',
  ...rest
}: BadgeProps) => {
  const { textColor, backgroundColor } = badgeStyles[variant];

  return (
    <div
      className={classNames([
        backgroundColor,
        'flex h-fit w-fit items-center',
        round ? 'rounded-full' : 'rounded-ds-xs',
        className,
        sizeStyles[size],
      ])}
      {...rest}
    >
      {leftIcon && (
        <div className={classNames([textColor, 'flex items-center px-0.5'])}>
          {React.cloneElement(leftIcon, { size: 11 })}
        </div>
      )}
      {size === 'sm' ? (
        <TextBadgeCompact
          uppercase
          color={textColor}
          label={label}
        />
      ) : (
        <TextBoxEditorTitle
          uppercase
          color={textColor}
          label={label}
        />
      )}
      {rightIcon && (
        <div className={classNames([textColor, 'flex items-center px-0.5'])}>
          {React.cloneElement(rightIcon, { size: 11 })}
        </div>
      )}
    </div>
  );
};
